import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * BrandedLayout — shared chrome for signed-out and pre-onboarding pages
 * (auth, legal, /onboarding/first-location).
 *
 * Mirrors the AppShell masthead (PharmaSync logo + two-tone wordmark on a
 * brand-sky tinted header) and the same legal-link footer, without any
 * AppShell dependencies on identity / org / nav. This is the layout the
 * pages that AppShell can't wrap (signed-out, onboarding before org bootstrap,
 * legal pages viewed by anyone) should use to stay on-brand.
 *
 * `homeHref` defaults to /auth/sign-in. `topRight` lets a host page (e.g.
 * FirstLocation) drop a Sign-out button or other action into the masthead.
 *
 * SignIn keeps its own marketing layout — it has the same wordmark pattern
 * but a richer two-column shell with carousel / FAQ / illustrations.
 */
export function BrandedLayout({
  children,
  homeHref = '/auth/sign-in',
  topRight,
}: {
  children: ReactNode;
  homeHref?: string;
  topRight?: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      <header
        className="border-b border-border"
        style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.18)' }}
      >
        <div className="container mx-auto flex items-center justify-between gap-4 py-4 px-4">
          <Link
            to={homeHref}
            className="flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="PharmaSync home"
          >
            <img src="/logo.svg" alt="" className="h-9 w-9" aria-hidden="true" />
            <h1 className="text-lg font-semibold tracking-tight leading-none">
              <span style={{ color: 'hsl(var(--brand-navy))' }}>Pharma</span>
              <span style={{ color: 'hsl(var(--brand-teal))' }}>Sync</span>
            </h1>
          </Link>
          {topRight ?? null}
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1">
        {children}
      </main>

      <footer
        className="container mx-auto py-6 px-4 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-4"
        aria-label="Legal links"
      >
        <Link
          to="/pricing"
          className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          Pricing
        </Link>
        <Link
          to="/privacy"
          className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          Privacy
        </Link>
        <Link
          to="/terms"
          className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          Terms
        </Link>
        <Link
          to="/dpa"
          className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          DPA
        </Link>
        <span className="ml-auto">© PharmaSync — Canadian pharmacy scheduling.</span>
      </footer>
    </div>
  );
}
